import { useState, useEffect, useCallback } from 'react';
import { Search, RotateCcw, MessageSquare, Shield, ShieldOff, Ban, Unlock, Crown, Users, Plus, X, FolderPlus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [resetDialog, setResetDialog] = useState(null);
  const [messageDialog, setMessageDialog] = useState(null);
  const [freezeDialog, setFreezeDialog] = useState(null);
  const [subDialog, setSubDialog] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [msgSubject, setMsgSubject] = useState('');
  const [msgBody, setMsgBody] = useState('');
  const [freezeReason, setFreezeReason] = useState('');
  const [subTier, setSubTier] = useState('free');
  const [graceDays, setGraceDays] = useState('');
  const [subNote, setSubNote] = useState('');
  const [disable2faDialog, setDisable2faDialog] = useState(null);

  // Group management state
  const [groups, setGroups] = useState([]);
  const [showGroupPanel, setShowGroupPanel] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [groupDialog, setGroupDialog] = useState(null);
  const [selectedGroupForUser, setSelectedGroupForUser] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users`, { params: { search } });
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch {
      toast.error('Failed to load users');
    }
    setLoading(false);
  }, [search]);

  const fetchGroups = async () => {
    try {
      const res = await axios.get(`${API}/admin/groups`);
      setGroups(res.data.groups);
    } catch {
      console.error('Failed to fetch groups');
    }
  };

  useEffect(() => { fetchUsers(); fetchGroups(); }, [fetchUsers]);

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    try {
      await axios.post(`${API}/admin/users/${resetDialog.id}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset for ${resetDialog.email}`);
      setResetDialog(null); setNewPassword('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reset password'); }
  };

  const handleSendMessage = async () => {
    if (!msgBody) { toast.error('Message is required'); return; }
    try {
      await axios.post(`${API}/admin/users/${messageDialog.id}/message`, { user_id: messageDialog.id, subject: msgSubject || 'Message from VerbNotes Team', message: msgBody });
      toast.success(`Message sent to ${messageDialog.name}`);
      setMessageDialog(null); setMsgSubject(''); setMsgBody('');
    } catch { toast.error('Failed to send message'); }
  };

  const handleFreeze = async () => {
    if (!freezeReason) { toast.error('Reason is required'); return; }
    try {
      await axios.post(`${API}/admin/users/${freezeDialog.id}/freeze`, { reason: freezeReason });
      toast.success(`Account ${freezeDialog.email} frozen`);
      setFreezeDialog(null); setFreezeReason(''); fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to freeze'); }
  };

  const handleUnfreeze = async (userId, email) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/unfreeze`);
      toast.success(`Account ${email} unfrozen`); fetchUsers();
    } catch { toast.error('Failed to unfreeze'); }
  };

  const handleSubUpdate = async () => {
    const data = { tier: subTier };
    if (graceDays) data.grace_period_days = parseInt(graceDays);
    if (subNote) data.admin_note = subNote;
    try {
      await axios.put(`${API}/admin/subscriptions/${subDialog.id}`, data);
      toast.success(`Subscription updated for ${subDialog.email}`);
      setSubDialog(null); setSubTier('free'); setGraceDays(''); setSubNote('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update subscription'); }
  };

  const handleDisable2fa = async () => {
    try {
      await axios.post(`${API}/admin/users/${disable2faDialog.id}/disable-2fa`);
      toast.success(`2FA disabled for ${disable2faDialog.email}`);
      setDisable2faDialog(null);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to disable 2FA'); }
  };

  // Group handlers
  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) { toast.error('Group name is required'); return; }
    setCreatingGroup(true);
    try {
      await axios.post(`${API}/admin/groups`, { name: newGroupName, description: newGroupDesc });
      toast.success('Group created'); setNewGroupName(''); setNewGroupDesc(''); fetchGroups();
    } catch { toast.error('Failed to create group'); }
    setCreatingGroup(false);
  };

  const handleDeleteGroup = async (groupId) => {
    try {
      await axios.delete(`${API}/admin/groups/${groupId}`);
      toast.success('Group deleted'); setGroups(groups.filter(g => g.id !== groupId));
      if (groupDialog?.id === groupId) setGroupDialog(null);
    } catch { toast.error('Failed to delete group'); }
  };

  const handleAddToGroup = async (groupId, userId) => {
    try {
      await axios.post(`${API}/admin/groups/${groupId}/members`, { user_ids: [userId] });
      toast.success('User added to group'); fetchGroups();
      setSelectedGroupForUser(null);
    } catch { toast.error('Failed to add user'); }
  };

  const handleRemoveFromGroup = async (groupId, userId) => {
    try {
      await axios.delete(`${API}/admin/groups/${groupId}/members/${userId}`);
      toast.success('User removed from group'); fetchGroups();
      if (groupDialog) {
        const res = await axios.get(`${API}/admin/groups`);
        const updated = res.data.groups.find(g => g.id === groupDialog.id);
        setGroupDialog(updated || null);
      }
    } catch { toast.error('Failed to remove user'); }
  };

  const getUserGroups = (userId) => groups.filter(g => g.member_ids?.includes(userId));

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h1 className="font-heading text-3xl text-white" data-testid="admin-users-title">Users ({total})</h1>
        <div className="flex items-center gap-3">
          <Button onClick={() => setShowGroupPanel(!showGroupPanel)} variant={showGroupPanel ? 'default' : 'outline'}
            className={showGroupPanel ? 'bg-cyan text-navy-950' : 'border-white/10 text-slate-300 hover:text-cyan'} data-testid="toggle-groups-panel">
            <Users size={16} className="mr-2" /> Groups ({groups.length})
          </Button>
          <div className="relative w-72">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input placeholder="Search by name or email..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 bg-navy-900 border-white/10 text-white" data-testid="admin-users-search" />
          </div>
        </div>
      </div>

      {/* Groups Panel */}
      {showGroupPanel && (
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Create Group Card */}
          <div className="glass rounded-xl p-4">
            <h3 className="text-white font-medium text-sm mb-3 flex items-center gap-2"><FolderPlus className="text-cyan" size={16} /> Create Group</h3>
            <div className="space-y-2">
              <Input value={newGroupName} onChange={e => setNewGroupName(e.target.value)} placeholder="Group name" className="bg-navy-950 border-white/10 text-white text-sm" data-testid="admin-group-name" />
              <Input value={newGroupDesc} onChange={e => setNewGroupDesc(e.target.value)} placeholder="Description (optional)" className="bg-navy-950 border-white/10 text-white text-sm" />
              <Button onClick={handleCreateGroup} disabled={creatingGroup} className="w-full bg-cyan/20 text-cyan hover:bg-cyan/30 text-sm" data-testid="admin-create-group-btn">
                {creatingGroup ? <Loader2 className="animate-spin" size={14} /> : <><Plus size={14} className="mr-1" /> Create</>}
              </Button>
            </div>
          </div>

          {/* Existing Groups */}
          {groups.map(g => (
            <div key={g.id} className="glass rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-white font-medium text-sm">{g.name}</h3>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-500 hover:text-cyan"
                    onClick={() => setGroupDialog(g)} data-testid={`admin-view-group-${g.id}`}><Users size={12} /></Button>
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-500 hover:text-red-400"
                    onClick={() => handleDeleteGroup(g.id)} data-testid={`admin-delete-group-${g.id}`}><X size={12} /></Button>
                </div>
              </div>
              {g.description && <p className="text-slate-500 text-xs mb-1">{g.description}</p>}
              <p className="text-cyan text-xs">{g.member_ids?.length || 0} members</p>
              {g.member_ids?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {g.member_ids.slice(0, 5).map(uid => {
                    const u = users.find(u => u.id === uid);
                    return <span key={uid} className="text-xs px-2 py-0.5 rounded-full bg-navy-950 text-slate-300 border border-white/5">{u?.name?.split(' ')[0] || uid.slice(0, 6)}</span>;
                  })}
                  {g.member_ids.length > 5 && <span className="text-xs text-slate-500">+{g.member_ids.length - 5}</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Users Table */}
      <div className="glass rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="admin-users-table">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left text-xs text-slate-400 font-medium px-4 py-3">Name</th>
                <th className="text-left text-xs text-slate-400 font-medium px-4 py-3">Email</th>
                <th className="text-left text-xs text-slate-400 font-medium px-4 py-3">Groups</th>
                <th className="text-left text-xs text-slate-400 font-medium px-4 py-3">Provider</th>
                <th className="text-left text-xs text-slate-400 font-medium px-4 py-3">Notes</th>
                <th className="text-left text-xs text-slate-400 font-medium px-4 py-3">Last Active</th>
                <th className="text-right text-xs text-slate-400 font-medium px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-8 text-slate-500">Loading...</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-slate-500">No users found</td></tr>
              ) : users.map(u => {
                const userGroups = getUserGroups(u.id);
                return (
                  <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {u.is_admin && <Shield size={14} className="text-red-400" />}
                        {u.is_frozen && <Ban size={14} className="text-red-500" />}
                        <span className={`text-sm ${u.is_frozen ? 'text-red-400 line-through' : 'text-white'}`}>{u.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-sm">{u.email}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1">
                        {userGroups.map(g => (
                          <span key={g.id} className="text-xs px-2 py-0.5 rounded-full bg-cyan/10 text-cyan border border-cyan/20">{g.name}</span>
                        ))}
                        <button onClick={() => setSelectedGroupForUser(selectedGroupForUser === u.id ? null : u.id)}
                          className="text-xs px-1.5 py-0.5 rounded text-slate-500 hover:text-cyan hover:bg-cyan/10 transition-colors" data-testid={`add-to-group-${u.id}`}>
                          <Plus size={12} />
                        </button>
                        {selectedGroupForUser === u.id && groups.length > 0 && (
                          <div className="absolute z-20 mt-1 bg-navy-900 border border-white/10 rounded-lg shadow-xl p-2 min-w-[160px]">
                            {groups.filter(g => !g.member_ids?.includes(u.id)).map(g => (
                              <button key={g.id} onClick={() => handleAddToGroup(g.id, u.id)}
                                className="w-full text-left px-3 py-1.5 text-sm text-slate-300 hover:text-white hover:bg-white/5 rounded">{g.name}</button>
                            ))}
                            {groups.filter(g => !g.member_ids?.includes(u.id)).length === 0 && (
                              <p className="text-xs text-slate-500 px-3 py-1.5">Already in all groups</p>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${u.provider === 'auth0' ? 'bg-purple-500/20 text-purple-300' : 'bg-cyan/20 text-cyan'}`}>{u.provider || 'email'}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-300 text-sm">{u.notes_count}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{u.last_active ? new Date(u.last_active).toLocaleDateString() : '-'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" className="text-slate-400 hover:text-cyan h-8 px-2" onClick={() => setMessageDialog(u)} title="Message" data-testid={`msg-user-${u.id}`}><MessageSquare size={14} /></Button>
                        <Button size="sm" variant="ghost" className="text-slate-400 hover:text-gold h-8 px-2" onClick={() => setSubDialog(u)} title="Subscription" data-testid={`sub-user-${u.id}`}><Crown size={14} /></Button>
                        {u.totp_enabled && (
                          <Button size="sm" variant="ghost" className="text-amber-400 hover:text-amber-300 h-8 px-2" onClick={() => setDisable2faDialog(u)} title="Disable 2FA" data-testid={`disable-2fa-${u.id}`}><ShieldOff size={14} /></Button>
                        )}
                        {!u.is_admin && (u.is_frozen ? (
                          <Button size="sm" variant="ghost" className="text-emerald-400 hover:text-emerald-300 h-8 px-2" onClick={() => handleUnfreeze(u.id, u.email)} title="Unfreeze" data-testid={`unfreeze-user-${u.id}`}><Unlock size={14} /></Button>
                        ) : (
                          <Button size="sm" variant="ghost" className="text-slate-400 hover:text-red-400 h-8 px-2" onClick={() => setFreezeDialog(u)} title="Freeze" data-testid={`freeze-user-${u.id}`}><Ban size={14} /></Button>
                        ))}
                        {u.provider !== 'auth0' && !u.is_admin && (
                          <Button size="sm" variant="ghost" className="text-slate-400 hover:text-gold h-8 px-2" onClick={() => setResetDialog(u)} title="Reset Password" data-testid={`reset-user-${u.id}`}><RotateCcw size={14} /></Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Group Members Dialog */}
      {groupDialog && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setGroupDialog(null)}>
          <div className="bg-navy-900 border border-white/10 rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-testid="admin-group-members-modal">
            <div className="flex items-center justify-between p-5 border-b border-white/5">
              <h3 className="text-white font-medium">{groupDialog.name} — Members</h3>
              <Button variant="ghost" size="icon" onClick={() => setGroupDialog(null)} className="text-slate-400 hover:text-white"><X size={18} /></Button>
            </div>
            <div className="p-5 max-h-[60vh] overflow-y-auto">
              <h4 className="text-slate-300 text-sm font-medium mb-3">Members ({groupDialog.member_ids?.length || 0})</h4>
              {groupDialog.member_ids?.length > 0 ? (
                <div className="space-y-2 mb-6">
                  {groupDialog.member_ids.map(uid => {
                    const u = users.find(u => u.id === uid);
                    return (
                      <div key={uid} className="flex items-center justify-between p-2 rounded-lg bg-navy-950/50 border border-white/5">
                        <div><p className="text-slate-200 text-sm">{u?.name || uid.slice(0, 8)}</p><p className="text-slate-500 text-xs">{u?.email || ''}</p></div>
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-500 hover:text-red-400" onClick={() => handleRemoveFromGroup(groupDialog.id, uid)} data-testid={`admin-remove-member-${uid}`}><X size={12} /></Button>
                      </div>
                    );
                  })}
                </div>
              ) : <p className="text-slate-500 text-sm mb-6">No members yet</p>}
              <h4 className="text-slate-300 text-sm font-medium mb-3">Add Users</h4>
              <div className="space-y-2">
                {users.filter(u => !groupDialog.member_ids?.includes(u.id)).map(u => (
                  <div key={u.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-white/[0.02]">
                    <div><p className="text-slate-200 text-sm">{u.name}</p><p className="text-slate-500 text-xs">{u.email}</p></div>
                    <Button variant="ghost" size="sm" className="text-cyan hover:bg-cyan/10" onClick={() => handleAddToGroup(groupDialog.id, u.id)} data-testid={`admin-add-member-${u.id}`}><Plus size={14} className="mr-1" /> Add</Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Dialog */}
      <Dialog open={!!resetDialog} onOpenChange={() => setResetDialog(null)}>
        <DialogContent className="bg-navy-900 border-white/10 text-white">
          <DialogHeader><DialogTitle>Reset Password for {resetDialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-slate-400 text-sm">{resetDialog?.email}</p>
            <div className="space-y-2">
              <Label className="text-slate-300">New Password</Label>
              <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 8 characters" className="bg-navy-950 border-white/10 text-white" data-testid="reset-password-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResetDialog(null)} className="text-slate-400">Cancel</Button>
            <Button onClick={handleResetPassword} className="bg-gold text-navy-950" data-testid="reset-password-confirm">Reset Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Message User Dialog */}
      <Dialog open={!!messageDialog} onOpenChange={() => setMessageDialog(null)}>
        <DialogContent className="bg-navy-900 border-white/10 text-white">
          <DialogHeader><DialogTitle>Message {messageDialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Subject</Label>
              <Input value={msgSubject} onChange={e => setMsgSubject(e.target.value)} placeholder="Message from VerbNotes Team" className="bg-navy-950 border-white/10 text-white" data-testid="msg-subject-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Message</Label>
              <Textarea value={msgBody} onChange={e => setMsgBody(e.target.value)} rows={4} placeholder="Write your message..." className="bg-navy-950 border-white/10 text-white resize-none" data-testid="msg-body-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMessageDialog(null)} className="text-slate-400">Cancel</Button>
            <Button onClick={handleSendMessage} className="bg-cyan text-navy-950" data-testid="msg-send-btn">Send Message</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Freeze Account Dialog */}
      <Dialog open={!!freezeDialog} onOpenChange={() => setFreezeDialog(null)}>
        <DialogContent className="bg-navy-900 border-white/10 text-white">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-400"><Ban size={18} /> Freeze Account</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-slate-400 text-sm">Freezing <strong className="text-white">{freezeDialog?.email}</strong> will block all account activity.</p>
            <div className="space-y-2">
              <Label className="text-slate-300">Reason for freezing</Label>
              <Textarea value={freezeReason} onChange={e => setFreezeReason(e.target.value)} rows={3} placeholder="e.g., Suspected fraudulent activity..." className="bg-navy-950 border-white/10 text-white resize-none" data-testid="freeze-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFreezeDialog(null)} className="text-slate-400">Cancel</Button>
            <Button variant="destructive" onClick={handleFreeze} data-testid="freeze-confirm-btn">Freeze Account</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscription Management Dialog */}
      <Dialog open={!!subDialog} onOpenChange={() => setSubDialog(null)}>
        <DialogContent className="bg-navy-900 border-white/10 text-white">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Crown size={18} className="text-gold" /> Manage Subscription</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-slate-400 text-sm">Managing subscription for <strong className="text-white">{subDialog?.email}</strong></p>
            <div className="space-y-2">
              <Label className="text-slate-300">Tier</Label>
              <select value={subTier} onChange={e => setSubTier(e.target.value)} className="w-full bg-navy-950 border border-white/10 text-white rounded-lg px-3 py-2 text-sm" data-testid="sub-tier-select">
                <option value="free">Free</option>
                <option value="basic">Basic ($9.99/mo)</option>
                <option value="premium">Premium ($25.99/mo)</option>
                <option value="lifetime">Lifetime ($299.99)</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Grace Period (days)</Label>
              <Input type="number" value={graceDays} onChange={e => setGraceDays(e.target.value)} placeholder="e.g., 7" className="bg-navy-950 border-white/10 text-white" data-testid="sub-grace-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Admin Note</Label>
              <Input value={subNote} onChange={e => setSubNote(e.target.value)} placeholder="Reason for adjustment..." className="bg-navy-950 border-white/10 text-white" data-testid="sub-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSubDialog(null)} className="text-slate-400">Cancel</Button>
            <Button onClick={handleSubUpdate} className="bg-gold text-navy-950" data-testid="sub-update-btn">Update Subscription</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Force Disable 2FA Dialog */}
      <Dialog open={!!disable2faDialog} onOpenChange={() => setDisable2faDialog(null)}>
        <DialogContent className="bg-navy-900 border-white/10 text-white">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-amber-400"><ShieldOff size={18} /> Force Disable 2FA</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-slate-400 text-sm">
              This will remove Two-Factor Authentication for <strong className="text-white">{disable2faDialog?.email}</strong>.
            </p>
            <p className="text-slate-400 text-sm">
              The user will be able to log in with just their password. They can re-enable 2FA from their settings.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDisable2faDialog(null)} className="text-slate-400">Cancel</Button>
            <Button onClick={handleDisable2fa} className="bg-amber-500 text-navy-950 hover:bg-amber-400" data-testid="disable-2fa-confirm-btn">Disable 2FA</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
